import { useState } from "react";
import { fmtDate } from "../format.ts";
import { safeHref } from "../url.ts";
import type { RtoBenchmarkRow } from "../metrics.ts";
import type { RtoItem, RtoStatus } from "../domain.ts";

// Card "Ready to Offer vs KPI" — nguồn: list "TA Ready to Offer" do TA log
// trên platform (nút ＋ Log RTO). Stock benchmark chỉ đếm ứng viên status
// Ready; KPI tháng đếm theo RTODate (flow) nên không phụ thuộc status.
//
// app.js vẫn giữ phần fetch + orchestration (confirm, toast, refetch); component
// chỉ nhận trạng thái đã quyết sẵn qua `phase` và bắn callback ra. Nhờ vậy
// không cần useEffect, và bước dời state sang App.tsx sau này không phải sửa
// lại markup ở đây.
export type RtoPhase =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  /** Admin chưa tick active tech stack nào → benchmark không có target để so. */
  | { kind: "no-stacks" }
  | {
      kind: "ready";
      bench: readonly RtoBenchmarkRow<RtoItem>[];
      totalRto: number;
      behind: number;
      withTarget: number;
    };

export type RtoBenchmarkProps = {
  min: number;
  max: number;
  phase: RtoPhase;
  onViewAll: () => void;
  /** Resolve = đã đổi xong (hoặc user bấm Cancel ở confirm). */
  onChangeStatus: (id: number, status: RtoStatus) => Promise<void>;
};

const ACTIONS: { status: RtoStatus; label: string; title: string }[] = [
  { status: "Offered", label: "Offer", title: "Đã gửi offer" },
  { status: "Hired", label: "Hire", title: "Đã nhận việc" },
  { status: "Rejected", label: "✕", title: "Từ chối / rớt" },
];

export function RtoBenchmark({ min, max, phase, onViewAll, onChangeStatus }: RtoBenchmarkProps) {
  // Khóa nút đang xử lý để không bắn 2 request trùng. State cục bộ của UI,
  // không phải state của dashboard.
  const [pending, setPending] = useState<number | null>(null);

  const head = (
    <div className="card-head">
      <h2>🎯 Ready to Offer vs KPI</h2>
      <div className="rto-head-right">
        <button className="btn-plain" id="rto-viewall" onClick={onViewAll}>
          ☰ Danh sách theo role
        </button>
        <span className="card-sub">TARGET {min}–{max} / ACTIVE STACK · TA LOG</span>
      </div>
    </div>
  );

  if (phase.kind === "loading") return <>{head}<div className="kpi-empty">Loading…</div></>;

  if (phase.kind === "error") {
    return <>{head}<div className="kpi-empty">Không tải được list RTO — {phase.message}</div></>;
  }

  if (phase.kind === "no-stacks") {
    return (
      <>
        {head}
        <div className="kpi-empty">
          Admin chưa chọn active tech stacks — vào <b>Admin → Active tech stacks</b> để tick
          các vị trí đang tuyển.
        </div>
      </>
    );
  }

  const { bench, totalRto, behind, withTarget } = phase;

  const candidate = (c: RtoItem) => {
    const href = safeHref(c.profileLink);
    const meta = [c.level, c.eng && `EN ${c.eng}`].filter(Boolean).join(" · ");
    return (
      <div className="rto-cand" key={c.id}>
        <div className="rto-cand-info">
          {href
            ? <a href={href} target="_blank" rel="noopener">{c.name}</a>
            : <b>{c.name}</b>}
          <small>{meta}</small>
          <small className="rto-meta">{c.recruiter} · RTO {fmtDate(c.rtoDate)}</small>
        </div>
        <div className="rto-actions">
          {ACTIONS.map((a) => (
            <button
              key={a.status}
              title={a.title}
              disabled={pending === c.id}
              onClick={async () => {
                setPending(c.id);
                try { await onChangeStatus(c.id, a.status); } finally { setPending(null); }
              }}
            >
              {a.label}
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      {head}
      <div className="pos-stats">
        <span className="pos-chip"><b>{totalRto}</b> candidates ready to offer</span>
        <span className="pos-chip red"><b>{behind}</b>/{withTarget} stacks dưới target</span>
        <span className="pos-chip green"><b>{withTarget - behind}</b> đạt target</span>
      </div>
      <div className="rto-grid">
        {bench.map((b) => {
          const tone = b.noTarget ? "filled" : (b.gap ? "red" : "ontrack");
          return (
            <div className={`pos-card ${tone}`} key={b.stack}>
              <div className="pos-head">
                <h3>{b.stack}</h3>
                {b.noTarget ? (
                  <span className="pos-badge filled">{b.rto} · NGOÀI ACTIVE</span>
                ) : (
                  <span className={`pos-badge ${b.gap ? "red" : "ontrack"}`}>
                    {b.gap ? `${b.rto}/${min} · CẦN THÊM ${b.gap}` : `${b.rto}/${min} ✓`}
                  </span>
                )}
              </div>
              {b.noTarget ? null : (
                <div className="progress">
                  <i className={b.gap ? "" : "hit"} style={{ width: `${Math.min(100, (b.rto / min) * 100)}%` }} />
                </div>
              )}
              {b.candidates.length ? (
                <div className="rto-names">{b.candidates.map(candidate)}</div>
              ) : (
                <div className="rto-names rto-empty">Chưa có ứng viên chờ offer</div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
